import { useCallback, useMemo, useState } from "react";

/**
 * Shared multi-select state for cockpit panels (Sprint 5v). Rows reveal a
 * checkbox on hover; selecting any row puts the panel into "bulk mode" with an
 * action bar. The hook owns the selected-id set, supports shift-click range
 * selection, and prunes ids that have left the list (after a move/archive) so
 * the action-bar count never lies.
 */
export interface BulkSelect<T extends { id: string }> {
  selectedIds: Set<string>;
  selectedItems: T[];
  count: number;
  /** True once at least one row is selected — panel is in bulk mode. */
  active: boolean;
  isSelected: (id: string) => boolean;
  /** Toggle a row; pass the click event to honour shift-range selection. */
  toggle: (id: string, e?: { shiftKey?: boolean }) => void;
  clear: () => void;
}

export function useBulkSelect<T extends { id: string }>(items: T[]): BulkSelect<T> {
  const [rawSelected, setRawSelected] = useState<Set<string>>(new Set());
  const [lastIndex, setLastIndex] = useState<number | null>(null);

  const toggle = useCallback(
    (id: string, e?: { shiftKey?: boolean }) => {
      const idx = items.findIndex((i) => i.id === id);
      setRawSelected((prev) => {
        const next = new Set(prev);
        if (e?.shiftKey && lastIndex !== null && idx !== -1) {
          const [lo, hi] = lastIndex < idx ? [lastIndex, idx] : [idx, lastIndex];
          for (let k = lo; k <= hi; k++) {
            const item = items[k];
            if (item) next.add(item.id);
          }
        } else if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
      if (idx !== -1) setLastIndex(idx);
    },
    [items, lastIndex],
  );

  const clear = useCallback(() => {
    setRawSelected(new Set());
    setLastIndex(null);
  }, []);

  // Prune ids that no longer exist in the current list.
  const selectedItems = useMemo(
    () => items.filter((i) => rawSelected.has(i.id)),
    [items, rawSelected],
  );
  const selectedIds = useMemo(() => new Set(selectedItems.map((i) => i.id)), [selectedItems]);

  return {
    selectedIds,
    selectedItems,
    count: selectedIds.size,
    active: selectedIds.size > 0,
    isSelected: (id: string) => selectedIds.has(id),
    toggle,
    clear,
  };
}
